import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import ReactSelect, { createFilter } from 'react-select-4';
import CreatableSelect from 'react-select-4/creatable';

import Option from './components/Option';
import Control from './components/Control';
import { styles } from './components/styles';
import { useSelectColor } from './hooks/useColor';
import { useTransformedValue } from './hooks/useTransformedValue';
import { useEnhancedOptions } from './hooks/useEnhancedOptions';
// TODO: Implement MenuList
// import MenuList from './SelectWrapper/components/MenuList';

const Select = ({
  id,
  value,
  name,
  placeholder,
  options,
  onChange,
  isMulti,
  isCreatable,
  isClearable,
  isInvalid,
  isSearchable,
  isLoading,
  isDisabled,
  addBlank,
  tooltipText,
  tooltipPlacement,
  tooltipClassName,
  filterOption,
  components: customComponents = {},
  ...restProps
}) => {
  const Component = isCreatable ? CreatableSelect : ReactSelect;

  const color = useSelectColor();
  const enhancedOptions = useEnhancedOptions(options, addBlank);
  const transformedValue = useTransformedValue(value, enhancedOptions, isMulti);

  const handleChange = useCallback((selected) => {
    onChange(selected);
  }, [onChange]);

  const mergedComponents = {
    Option,
    Control,
    ...customComponents,
  };

  const finalFilterOption = filterOption === undefined
    ? createFilter({ ignoreAccents: false })
    : filterOption;

  return (
    <>
      <Component
        openMenuOnFocus
        inputId={id}
        name={name}
        filterOption={finalFilterOption}
        components={mergedComponents}
        placeholder={placeholder}
        options={enhancedOptions}
        onChange={handleChange}
        value={transformedValue}
        isMulti={isMulti}
        closeMenuOnSelect={!isMulti}
        isClearable={isClearable}
        isSearchable={isSearchable}
        isDisabled={isDisabled}
        isLoading={isLoading}
        styles={styles(color, isInvalid)}
        tooltipText={tooltipText}
        tooltipPlacement={tooltipPlacement}
        tooltipClassName={tooltipClassName}
        {...restProps}
      />
    </>
  );
};

Select.defaultProps = {
  id:               '',
  value:            '',
  name:             'dropdown',
  placeholder:      'Select an Option',
  onChange:         () => false,
  options:          [],
  filterOption:     undefined,
  components:       {},
  isCreatable:      false,
  isMulti:          false,
  isClearable:      false,
  isInvalid:        false,
  isSearchable:     false,
  isLoading:        false,
  addBlank:         false,
  tooltipText:      '',
  tooltipPlacement: 'bottom',
  tooltipClassName: '',
};

Select.propTypes = {
  id:           PropTypes.string,
  value:        PropTypes.oneOfType([
    PropTypes.shape({}),
    PropTypes.array,
    PropTypes.string,
    PropTypes.number,
  ]),
  name:         PropTypes.string,
  placeholder:  PropTypes.string,
  onChange:     PropTypes.func,
  options:      PropTypes.arrayOf(PropTypes.shape({})),
  filterOption: PropTypes.func,
  components:   PropTypes.shape({}),
  isCreatable:  PropTypes.bool,
  isMulti:      PropTypes.bool,
  isClearable:  PropTypes.bool,
  isInvalid:    PropTypes.bool,
  isSearchable: PropTypes.bool,
  isLoading:    PropTypes.bool,
  addBlank:     PropTypes.bool,
  tooltipText:  PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({}),
  ]),
  tooltipPlacement: PropTypes.string,
  tooltipClassName: PropTypes.string,
};

export default Select;
